import dayjs from "dayjs";
import * as yup from "yup";
import { ILocation, InvoiceType, RentalLocationType, RentalType } from "../../types";
import { checkClosedDMS, FISCAL_CODE_REGEXP, FISCAL_CODE_REGEXP_ES, FISCAL_CODE_REGEXP_PT, getRequiredVatNr, validateLocationDate, validateRangeDate } from "../../utils";
import { InsertBookingStep } from "./types";
import { RESPONSE_CHECK_CODE } from "../../api/services";
import { ITALY_CODE, SPAIN_CODE, PORTUGAL_CODE } from "../../../network/config";
import { convertUtcToTzIsoDayJs } from "../../../../util/timezoneConversion";

export const validationSchema = yup.object({
    basicDetails: yup
        .object({
            rentalType: yup
                .string()
                .required("message.validation.required")
            ,
            pickupLocation: yup
                .object()
                .nullable()
                .required("message.validation.required")
            ,
            pickupLocationTimezone: yup
                .string()
                .nullable()
            ,
            dropoffLocation: yup
                .object()
                .nullable()
                .required("message.validation.required")
            ,
            bookingDateTime: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .test(
                    'maxDate',
                    'message.validation.date_not_allowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        if (!businessContinuityMode || date == null)
                            return true;

                        return date?.isBefore(dayjs());
                    }
                ),
            pickupDate: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .required("message.validation.required")
                .test(
                    'testAfterBookingDateTime',
                    'message.validation.pickup_date_after_booking_date_time',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const bookingDateTime: dayjs.Dayjs | null = !!context.parent.bookingDateTime ? dayjs(context?.parent?.bookingDateTime) : null;
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        if (!businessContinuityMode || date == null || bookingDateTime == null)
                            return true;

                        return date.isAfter(bookingDateTime);
                    }
                )
                .test(
                    'testClosedDMS',
                    'message.validation.closedDMS',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        var timezone: string = context?.parent?.pickupLocationTimezone ?? undefined;
                        const currDayJsTimezoned = convertUtcToTzIsoDayJs(dayjs().toISOString(), timezone);
                        return (((!!businessContinuityMode && !!date?.isBefore(currDayJsTimezoned)) || (!businessContinuityMode && !!date?.isAfter(currDayJsTimezoned))))
                        && checkClosedDMS(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                ).test(
                    'testOpenings',
                    'message.validation.afterHoursNotAllowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return validateLocationDate(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                )
                .test(
                    'testClosedDMS',
                    'message.validation.closedDMS',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return checkClosedDMS(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                ).test(
                    'testOpenings',
                    'message.validation.afterHoursNotAllowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return validateLocationDate(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                )
                .test(
                    'testClosedDMS',
                    'message.validation.closedDMS',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return checkClosedDMS(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                ).test(
                    'testOpenings',
                    'message.validation.afterHoursNotAllowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return validateLocationDate(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator);
                    }
                ),
            dropoffDate: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .required("message.validation.required")
                .test(
                    'minDate',
                    'message.validation.dropoff_date_greater_pickup_date',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const pickupDate: dayjs.Dayjs | null = context.parent.pickupDate ? dayjs(context.parent.pickupDate) : null;
                        if (!date || !pickupDate || !pickupDate.isValid()) {
                            return true;
                        }
                        return date.isAfter(pickupDate);
                    }
                )
                .test(
                    'max30Days',
                    'message.validation.dropoff_date_over_30_days_pickup_date',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const pickupDate: dayjs.Dayjs | null = context.parent.pickupDate ? dayjs(context.parent.pickupDate) : null;
                        if (!date || !pickupDate || !pickupDate.isValid()) {
                            return true;
                        }
                        return date.isBefore(pickupDate.add(30, "day").add(1, 'm'));
                    }
                )
                .test(
                    'testEndRangeDate',
                    'message.validation.date_not_allowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const endDate: dayjs.Dayjs | null = !!context?.options?.context?.rangeDate?.endDate ? dayjs(context?.options?.context?.rangeDate?.endDate) : null;
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        var timezone: string = context?.parent?.pickupLocationTimezone ?? undefined;
                        const currDayJsTimezoned = convertUtcToTzIsoDayJs(dayjs().toISOString(), timezone);
                        return ((!!businessContinuityMode && !!date?.isBefore(currDayJsTimezoned)) || !!date?.isAfter(currDayJsTimezoned))
                        && validateRangeDate(date, endDate, RentalLocationType.Dropoff);
                    }
                ).test(
                    'testClosedDMS',
                    'message.validation.closedDMS',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.dropoffLocation;
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return checkClosedDMS(date, selectedLocation, RentalLocationType.Dropoff);
                    }
                ).test(
                    'testOpenings',
                    'message.validation.afterHoursNotAllowed',
                    (value, context) => {
                        const date = value ? dayjs(value) : null;
                        const selectedLocation: ILocation | null = context.parent.dropoffLocation;
                        const businessContinuityMode = context?.options?.context?.businessContinuityMode;
                        return validateLocationDate(date, selectedLocation, RentalLocationType.Dropoff);
                    }
                ),
            vehicleCategory: yup.string()
                .required("message.validation.required"),
            carGroup: yup.object()
                .nullable()
                .required("message.validation.required"),
            mileageIncluded: yup.string()
                .nullable()
                .required("message.validation.required"),
            customerCode: yup.string()
                .nullable()
                .test(
                    'testCustomerCodeRequired',
                    'message.validation.required',
                    (value, context) => {
                        return !(context?.parent.rentalType === RentalType.CORPORATE && !value);
                    }
                )
                .test(
                    'testCustomerCodeCheckValid',
                    'message.validation.invalidated_code',
                    (value, context) => {
                        return !(context?.parent.rentalType === RentalType.CORPORATE && !context?.options?.context?.bACheckValidate);
                    }
                ),
            dossierNumber: yup
                .string()
                .nullable()
                .test(
                    'testMandatoryDossier',
                    'message.validation.required',
                    (value, context) => {
                        return !(!!context?.options?.context?.mandatoryDossierRequired && context?.parent.rentalType === RentalType.CORPORATE && !value);
                    }
                ),
            cig: yup
                .string()
                .nullable()
                .test(
                    'testCig',
                    'message.validation.required',
                    (value, context) => {
                        return !(context?.options?.context?.hasCig && context?.parent.rentalType === RentalType.CORPORATE && !value);
                    }
                ),
            // tenants: yup
            //     .array()
            //     .min(1, 'message.validation.required')
            //     .max(1),
            // dealers: yup
            //     .array()
            //     .min(1, 'message.validation.required')
            //     .max(1),
            tenants: yup
                .string()
                .nullable()
                .required('message.validation.required'),
            dealers: yup
                .string()
                .nullable()
                .required('message.validation.required'),
        }),
    extraServices: yup
        .object({
            note: yup
                .string()
                .when('discountOverprice', {
                    is: null,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            deliveryStreetName: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            deliveryStreetNumber: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            deliveryPostalCode: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            deliveryCity: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            deliveryState: yup
                .string()
                .when('deliveryStreetName', {
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            collectionStreetName: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            collectionStreetNumber: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            collectionPostalCode: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            collectionCity: yup
                .string()
                .when({
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            collectionState: yup
                .string()
                .nullable()
                .when('collectionStreetName', {
                    is: undefined,
                    then: schema => schema,
                    otherwise: schema => schema.required('message.validation.required')
                }),
            discountOverprice: yup
                .number()
                .nullable()
                .test(
                    'verifyDiscount',
                    'message.validation.invalid_insert_promo_discount_overprice',
                    (value, context) => {
                        return !context.parent.discountType || !value;
                    }
                ),
            discountType: yup
                .string()
                .nullable()
                .test(
                    'verifyPromo',
                    'message.validation.invalid_insert_promo_discount_overprice',
                    (value, context) => {
                        return !context.parent.discountOverprice || !value;
                    }
                ),
            discountCode: yup
                .string()
                .when('discountType', {
                    is: 'promo',
                    then: schema => schema.required('message.validation.required'),
                    otherwise: schema => schema
                })
                .test(
                    'verifyIsCheck',
                    'message.validation.check_code',
                    (value, context) => {
                        // return !(!context?.options?.context?.couponQuery?.data && !!value);
                        return !(!context.parent.checkCoupon && !!value);
                    }
                )
                .test(
                    'discountCodeIsInvalid',
                    'message.validation.invalid_code',
                    (value, context) => {
                        // return !(context?.options?.context?.couponQuery?.data?.code !== RESPONSE_CHECK_CODE.SUCCESS && !!value);
                        return !(context.parent.checkCoupon?.code !== RESPONSE_CHECK_CODE.SUCCESS && !!value);
                    }
                ),
            fcaBankCardNumber: yup
                .string()
                .when('discountType', {
                    is: 'fca',
                    then: schema => schema.required('message.validation.required'),
                    otherwise: schema => schema
                }),
            affiliationCode: yup
                .string()
                .nullable()
                .test(
                    'verifyIsCheck',
                    'message.validation.check_affiliation_code',
                    (value, context) => {
                        // return !(!context?.options?.context?.checkAffiliatedQuery?.data && !!value);
                        return !(!context.parent.checkAffiliated && !!value);
                    }
                )
                .test(
                    'affiliationCodeIsInvalid',
                    'message.validation.invalid_code',
                    (value, context) => {
                        // return !(!context?.options?.context?.checkAffiliatedQuery?.data?.valid && !!value);
                        return !(!context.parent.checkAffiliated?.valid && !!value);
                    }
                )
        }),
    firstDriver: yup
        .object()
        .when('$step', {
            is: InsertBookingStep.CUSTOMER_DETAILS,
            then: (schema) => schema
                .shape({
                    name: yup
                        .string()
                        .required('message.validation.required')
                    ,
                    surname: yup
                        .string()
                        .required('message.validation.required')
                    ,
                    birthDate: yup
                        .date()
                        .typeError('message.validation.invalid_date')
                        .nullable()
                        .test(
                            'testBirthDateIsRequired',
                            'message.validation.required',
                            (value, context) => {
                                // @ts-ignore
                                const rentalType = context?.from[1].value?.basicDetails?.rentalType;
                                return !(rentalType === RentalType.RETAIL && !value);
                            }
                        ),
                    email: yup
                        .string()
                        .email('message.validation.invalid_email')
                        .test(
                            'testEmailIsRequired',
                            'message.validation.required',
                            (value, context) => {
                                // @ts-ignore
                                const rentalType = context?.from[1].value?.basicDetails?.rentalType;
                                return !(rentalType === RentalType.RETAIL && !value);
                            }
                        ),
                    mobile: yup
                        .string()
                        .test(
                            'testMobileIsRequired',
                            'message.validation.required',
                            (value, context) => {
                                // @ts-ignore
                                const rentalType = context?.from[1].value?.basicDetails?.rentalType;
                                return !(rentalType === RentalType.RETAIL && !value);
                            }
                        ),
                    fiscalCode: yup
                        .string()
                        .when('residenceCountry', (residenceCountry, schema) => {
                            const regexMap: Record<string, RegExp> = {
                                [SPAIN_CODE]: FISCAL_CODE_REGEXP_ES,
                                [PORTUGAL_CODE]: FISCAL_CODE_REGEXP_PT
                            };

                            const regex = regexMap[residenceCountry] || FISCAL_CODE_REGEXP;

                            return schema.matches(regex, {
                                message: 'message.validation.invalid_fiscal_code',
                                excludeEmptyString: true
                            });
                        }),
                    mobilePrefix: yup
                        .object()
                        .nullable()
                        .test(
                            'testMobilePrefixIsRequired',
                            'message.validation.required',
                            (value, context) => {
                                // @ts-ignore
                                const rentalType = context?.from[2].value?.basicDetails?.rentalType;
                                return !(rentalType === RentalType.RETAIL && !value);
                            }
                        )
                }),
            otherwise: (schema) => schema
        }),
    invoice: yup
        .object()
        .when('$step', {
            is: InsertBookingStep.CUSTOMER_DETAILS,
            then: schema => schema
                .shape({
                    birthDate: yup
                        .mixed()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.PERSON,
                            then: yup
                                .date()
                                .typeError('message.validation.invalid_date')
                                .nullable()
                        }),
                    email: yup
                        .string()
                        .email('message.validation.invalid_email')
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: schema => schema
                                .required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    billingCountry: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    billingProvince: yup
                        .string()
                        .nullable()
                        .test(
                            'provinceRequired',
                            'message.validation.required',
                            (value, context) => {
                                if ((value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY) && (context.parent.vatNumberCountry === ITALY_CODE)) {
                                    return false
                                }
                                return true
                            }
                        ),
                    billingCity: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    billingStreetName: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    billingStreetNumber: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    billingPostalCode: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    vatNumber: yup
                        .string()
                        .nullable()
                        .when(['type', 'legalEntity'], {
                            is: (type: InvoiceType, legalEntity: any) => (
                                ((type === InvoiceType.VAT_NUMBER_HOLDER || type === InvoiceType.COMPANY) && getRequiredVatNr(legalEntity?.code))
                            ),
                            then: (schema) => {
                                return schema.required('message.validation.required')
                            },
                            otherwise: schema => schema
                        }),
                    vatNumberCountry: yup
                        .string()
                        .nullable()
                        .when(['type', 'legalEntity'], {
                            is: (type: InvoiceType, legalEntity: any) => (
                                ((type === InvoiceType.VAT_NUMBER_HOLDER || type === InvoiceType.COMPANY) && getRequiredVatNr(legalEntity?.code))
                            ),
                            then: (schema) => {
                                return schema.required('message.validation.required')
                            },
                            otherwise: schema => schema
                        }),
                    companyName: yup
                        .string()
                        .when('type', {
                            is: (value: InvoiceType) => value === InvoiceType.VAT_NUMBER_HOLDER || value === InvoiceType.COMPANY,
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    fiscalCode: yup
                        .string()
                        .nullable()
                        .when('type', {
                            is: InvoiceType.PERSON,
                            then: (schema) => schema
                                .when('residenceCountry', (residenceCountry, schema) => {
                                    const regexMap: Record<string, RegExp> = {
                                        [SPAIN_CODE]: FISCAL_CODE_REGEXP_ES,
                                        [PORTUGAL_CODE]: FISCAL_CODE_REGEXP_PT
                                    };

                                    const regex = regexMap[residenceCountry] || FISCAL_CODE_REGEXP;

                                    return schema.matches(regex, {
                                        message: 'message.validation.invalid_fiscal_code',
                                        excludeEmptyString: true
                                    });
                                })
                        })
                        .when(['type', 'vatNumberCountry'], {
                            is: (type: InvoiceType, vatNumberCountry: string) =>
                                (type === InvoiceType.VAT_NUMBER_HOLDER || type === InvoiceType.COMPANY) &&
                                vatNumberCountry === ITALY_CODE,
                            then: (schema) => schema.required('message.validation.required')
                        }),
                    certifiedEmail: yup
                        .string()
                        .nullable()
                        .when(['type', 'invoiceCode', 'publicAuthority', 'billingCountry'], {
                            is: (type: InvoiceType, invoiceCode: string, publicAuthority: boolean, billingCountry: string) => (
                                (type === InvoiceType.VAT_NUMBER_HOLDER || type === InvoiceType.COMPANY) && !publicAuthority && invoiceCode === '' && billingCountry === 'IT'
                            ),
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        })
                        .when(['type', 'paIdNumber', 'publicAuthority', 'billingCountry'], {
                            is: (type: InvoiceType, paIdNumber: string, publicAuthority: boolean, billingCountry: string) => (
                                type === InvoiceType.COMPANY && publicAuthority && paIdNumber === '' && billingCountry === 'IT'
                            ),
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    invoiceCode: yup
                        .string()
                        .when(['type', 'certifiedEmail', 'publicAuthority', 'billingCountry'], {
                            is: (type: InvoiceType, certifiedEmail: string, publicAuthority: boolean, billingCountry: string) => (
                                (type === InvoiceType.VAT_NUMBER_HOLDER || type === InvoiceType.COMPANY) && !publicAuthority && certifiedEmail === '' && billingCountry === 'IT'
                            ),
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                    paIdNumber: yup
                        .string()
                        .when(['type', 'certifiedEmail', 'publicAuthority'], {
                            is: (type: InvoiceType, certifiedEmail: string, publicAuthority: boolean) => (
                                type === InvoiceType.COMPANY && publicAuthority && certifiedEmail === ''
                            ),
                            then: (schema) => schema.required('message.validation.required'),
                            otherwise: schema => schema
                        }),
                },
                    [
                        ['certifiedEmail', 'invoiceCode'],
                        ['certifiedEmail', 'paIdNumber'],
                    ]
                ),
            otherwise: schema => schema
        })
});
